import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';

import IdentityWorkspace from '../components/identity/IdentityWorkspace';
import { useMigrationDetailQuery } from '../hooks/useMigrationDetail';

export default function MigrationIdentitiesPage() {
  const { migrationId } = useParams<{ migrationId: string }>();
  const location = useLocation();
  const query = useMigrationDetailQuery(migrationId);

  // Chip → row cross-link across the route split (FD-127). The Devices grid's
  // Mapping chip navigates here with the target row's DOM id in the URL hash
  // (`#mapping-row-<normalizedSid>` — IdentityMappingsSection emits the same id
  // via rowDomId). The mapping table renders asynchronously once the suggestions
  // query resolves, so poll a bounded number of animation frames for the target
  // before scrolling to it.
  useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    let raf = 0;
    let tries = 0;
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (tries++ < 60) raf = requestAnimationFrame(attempt);
    };
    raf = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(raf);
  }, [location.hash]);

  if (!query.data || !migrationId) return null;

  const { migration, devices } = query.data;
  const migrationType = migration.migrationType;
  const isMappingType = migrationType === 'T2T' || migrationType === 'D2C';

  if (!isMappingType) {
    return (
      <div className="max-w-xl">
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>No identity mappings for this migration type</MessageBarTitle>
            Identity reconciliation applies to tenant-to-tenant (T2T) and domain-to-cloud
            (D2C) migrations only.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  // FD-380 — the workspace itself is shared with Resources › Identities
  // (FD-ADR-037 §1). This page contributes the campaign context and the
  // cross-link scroll; it owns no editing logic.
  //
  // `csvControls="none"` because the campaign layout carries "Download
  // template" and "Import CSV" in its `···` menu, under an "Identity mappings"
  // group header. Two import buttons with two independent result bars is worse
  // than one.
  return (
    <IdentityWorkspace
      migrationId={migrationId}
      migrationType={migrationType as 'T2T' | 'D2C'}
      csvControls="none"
      bprtStatus={migration.bprtStatus}
      deviceCount={devices.length}
      sourceTenantDomain={migration.sourceTenantDomain}
      sourceTenantId={migration.sourceTenantId}
      destTenantDomain={migration.destTenantDomain}
      destTenantId={migration.destTenantId}
    />
  );
}
